import { useState, useEffect } from "react";
import { Nav, Footer, GlobalStyles, theme, getCareConfig } from "./design-system";

export default function ConditionsPage() {
  const [conditions, setConditions] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [query, setQuery]           = useState("");
  const [hovered, setHovered]       = useState(null);
  const [focused, setFocused]       = useState(false);

  useEffect(() => {
    fetch("https://co-6008-ai-chatbot-5yr6.vercel.app/api/conditions")
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setConditions(d.conditions); setLoading(false); })
      .catch(() => { setError("Could not load conditions. Is the backend running?"); setLoading(false); });
  }, []);

  const filtered = conditions.filter(c =>
    c.name.toLowerCase().includes(query.toLowerCase()) ||
    c.category.toLowerCase().includes(query.toLowerCase())
  );

  // Map action string to care level key
  const actionToKey = (action = "") => {
    const a = action.toLowerCase();
    if (a.includes("999") || a.includes("a&e"))     return "emergency";
    if (a.includes("111"))                           return "nhs111";
    if (a.includes("pharmacy"))                      return "pharmacy";
    if (a.includes("dentist"))                       return "dentist";
    if (a.includes("gp") || a.includes("miu"))       return "gp";
    return "self-care";
  };

  return (
    <div style={{ fontFamily: theme.fontBody, background: theme.bg, minHeight: "100vh" }}>
      <GlobalStyles />
      <Nav active="conditions" />

      {/* HEADER */}
      <div style={{ padding: "4rem 2rem 3rem", background: theme.bg, borderBottom: `1px solid ${theme.border}` }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <p style={{ color: theme.clay, fontSize: "0.7rem", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.12em", margin: "0 0 0.65rem" }}>Health A to Z</p>
          <h1 style={{ fontFamily: theme.fontDisplay, fontSize: "clamp(2rem, 4vw, 2.8rem)", fontWeight: "500", color: theme.text, margin: "0 0 0.75rem", lineHeight: 1.2 }}>
            Conditions Library
          </h1>
          <p style={{ color: theme.textMid, fontSize: "0.93rem", lineHeight: 1.7, margin: "0 0 2rem", maxWidth: "480px" }}>
            Browse all 60 common conditions. Select one to read symptoms, self-care guidance, and when to seek further help.
          </p>

          {/* Search */}
          <div style={{ maxWidth: "460px", position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center", background: theme.surface, border: `1.5px solid ${focused ? theme.clay : theme.border}`, borderRadius: theme.radiusPill, padding: "0.45rem 0.45rem 0.45rem 1.2rem", boxShadow: focused ? `0 0 0 4px ${theme.clayLight}` : theme.shadow, transition: "all 0.2s" }}>
              <span style={{ color: theme.textLight, marginRight: "0.7rem", fontSize: "0.9rem" }}>🔍</span>
              <input
                type="text" value={query} onChange={e => setQuery(e.target.value)}
                onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
                placeholder="Search conditions or categories..."
                style={{ flex: 1, border: "none", outline: "none", fontSize: "0.88rem", fontFamily: theme.fontBody, color: theme.text, background: "transparent" }}
              />
              {query && (
                <button onClick={() => setQuery("")} style={{ background: "none", border: "none", color: theme.textLight, cursor: "pointer", padding: "0 0.65rem", fontSize: "0.8rem", fontFamily: theme.fontBody }}>Clear</button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* RESULTS BAR */}
      <div style={{ background: theme.surface, borderBottom: `1px solid ${theme.border}`, padding: "0.7rem 2rem" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem" }}>
          <p style={{ color: theme.textLight, fontSize: "0.78rem", margin: 0 }}>
            {loading ? "Loading..." : query ? `${filtered.length} result${filtered.length !== 1 ? "s" : ""} for "${query}"` : `${conditions.length} conditions`}
          </p>
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            {[["Self-care", theme.green], ["Pharmacy", "#4A6A8A"], ["GP advised", theme.amber], ["A&E / 999", theme.red]].map(([label, color]) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: "0.32rem" }}>
                <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: color }} />
                <span style={{ fontSize: "0.7rem", color: theme.textLight }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* GRID */}
      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "2rem" }}>
        {loading && (
          <div style={{ textAlign: "center", padding: "4rem" }}>
            <div style={{ width: "34px", height: "34px", border: `2.5px solid ${theme.border}`, borderTop: `2.5px solid ${theme.clay}`, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 1rem" }} />
            <p style={{ color: theme.textLight, fontSize: "0.87rem" }}>Loading conditions...</p>
          </div>
        )}

        {error && (
          <div style={{ background: theme.redLight, border: `1px solid ${theme.red}44`, borderRadius: theme.radius, padding: "1.25rem", color: theme.red, fontSize: "0.88rem" }}>{error}</div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "4rem" }}>
            <p style={{ fontFamily: theme.fontDisplay, fontSize: "1.2rem", color: theme.text, marginBottom: "0.5rem" }}>No conditions found</p>
            <p style={{ color: theme.textMid, fontSize: "0.85rem", marginBottom: "1.25rem" }}>Try a different search term.</p>
            <button onClick={() => setQuery("")} style={{ background: theme.clay, color: "#fff", border: "none", padding: "0.55rem 1.2rem", borderRadius: theme.radiusPill, fontFamily: theme.fontBody, fontWeight: "600", fontSize: "0.85rem", cursor: "pointer" }}>Clear search</button>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(258px, 1fr))", gap: "0.95rem" }}>
            {filtered.map(condition => {
              const key  = actionToKey(condition.action);
              const care = getCareConfig(key);
              const isHov = hovered === condition.id;
              return (
                <a
                  key={condition.id}
                  href={`/conditions/${condition.id}`}
                  onMouseEnter={() => setHovered(condition.id)}
                  onMouseLeave={() => setHovered(null)}
                  style={{
                    background: isHov ? theme.surface : theme.bg,
                    borderRadius: theme.radius,
                    padding: "1.2rem 1.4rem",
                    textDecoration: "none",
                    display: "flex", flexDirection: "column", gap: "0.55rem",
                    border: `1px solid ${isHov ? theme.clay : theme.border}`,
                    borderTop: `3px solid ${isHov ? theme.clay : theme.border}`,
                    boxShadow: isHov ? theme.shadowMd : theme.shadow,
                    transition: "all 0.15s",
                    transform: isHov ? "translateY(-2px)" : "translateY(0)",
                  }}
                >
                  <span style={{ alignSelf: "flex-start", background: theme.bgDeep, color: theme.textMid, fontSize: "0.65rem", fontWeight: "600", padding: "0.18rem 0.55rem", borderRadius: theme.radiusPill, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    {condition.category}
                  </span>
                  <h3 style={{ margin: 0, fontSize: "0.9rem", fontWeight: "600", lineHeight: 1.35, color: isHov ? theme.clay : theme.text, transition: "color 0.15s" }}>
                    {condition.name}
                  </h3>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "auto", paddingTop: "0.2rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.38rem" }}>
                      <div style={{ width: "5px", height: "5px", borderRadius: "50%", background: care.dot, flexShrink: 0 }} />
                      <span style={{ color: care.text, fontSize: "0.67rem", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {condition.action}
                      </span>
                    </div>
                    <span style={{ color: isHov ? theme.clay : theme.textLight, fontSize: "1rem", lineHeight: 1 }}>›</span>
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}